import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Input, 
  Button, 
  Card, 
  Tag, 
  Space, 
  Pagination, 
  message,
  Modal,
  Table,
  Image,
  Spin,
  Empty
} from 'antd';
import { 
  SearchOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { productGroupService } from '../services/productGroupService';
import type { ProductGroup } from '../types/productGroup';
import type { Product } from '../types/product';
import {
  getProductGroupCategoryLabel,
  pickProductGroupCategoryFields,
} from '../constants/productGroupCategory';
import {
  getCategoryLabel,
  getChildCategoryLabel,
  pickChildCategoryFields,
} from '../constants/productCategory';
import { useLanguage } from '../contexts/LanguageContext';

const { Search } = Input;

const ProductGroupList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  const [expandedProducts, setExpandedProducts] = useState<Record<string, Product[]>>({});
  const [expandingGroupIds, setExpandingGroupIds] = useState<Set<string>>(new Set());

  const loadGroupProducts = useCallback(async (groupId: string, force = false) => {
    if (!force && expandedProducts[groupId]) {
      return;
    }

    setExpandingGroupIds((prev) => new Set(prev).add(groupId));
    try {
      const response = await productGroupService.getGroupProducts(groupId);
      const products: Product[] = response.data || [];
      setExpandedProducts((prev) => ({ ...prev, [groupId]: products }));
    } catch (error) {
      message.error(t('msgFetchRelatedProductsFailed'));
    } finally {
      setExpandingGroupIds((prev) => {
        const next = new Set(prev);
        next.delete(groupId);
        return next;
      });
    }
  }, [expandedProducts]);

  // 获取商品组列表
  const fetchProductGroups = async (page = 1, size = 20, search = '') => {
    setLoading(true);
    try {
      const response = await productGroupService.getProductGroupList({
        pageNo: page,
        pageSize: size,
        search
      });
      setProductGroups(response.data.records || []);
      setTotal(response.data.total || 0);
      setExpandedProducts({});
    } catch (error) {
      message.error(t('msgFetchProductGroupListFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductGroups(current, pageSize, searchText);
  }, [current, pageSize]);

  // 搜索
  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrent(1);
    fetchProductGroups(1, pageSize, value);
  };

  // 新增商品组
  const handleAdd = () => {
    navigate('/product-group/add');
  };

  // 编辑商品组
  const handleEdit = (id: string) => {
    navigate(`/product-group/edit/${id}`);
  };

  // 查看商品组详情
  const handleView = (id: string) => {
    navigate(`/product-group/detail/${id}`);
  };

  // 删除商品组
  const handleDelete = (id: string) => {
    Modal.confirm({
      title: t('confirmDeleteTitle'),
      content: t('confirmDeleteProductGroup'),
      onOk: async () => {
        try {
          await productGroupService.deleteProductGroup(id);
          message.success(t('msgDeleteSuccess'));
          fetchProductGroups(current, pageSize, searchText);
        } catch (error) {
          message.error(t('msgDeleteFailed'));
        }
      },
    });
  };

  const formatDateTime = (date?: string) =>
    date
      ? new Date(date)
          .toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          })
          .replace(/\//g, '-')
      : '--';

  // 表格列配置
  const columns = useMemo(() => [
    {
      title: t('productGroupCode'),
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (id: string) => (
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: '12px',
            color: '#666',
            backgroundColor: '#f5f5f5',
            padding: '2px 6px',
            borderRadius: '4px',
          }}
        >
          {id}
        </span>
      ),
    },
    {
      title: t('productGroupInfo'),
      key: 'productGroup',
      width: 200,
      ellipsis: true,
      render: (record: ProductGroup) => (
        <div style={{ display: 'flex', alignItems: 'center', maxWidth: 200 }}>
          {record.logoUrl ? (
            <Image
              width={44}
              height={44}
              src={record.logoUrl}
              alt={record.name}
              style={{
                borderRadius: '6px',
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: '6px',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                fontSize: '11px',
                flexShrink: 0,
              }}
            >
              {t('emptyNoImage')}
            </div>
          )}
          <div style={{ marginLeft: 8, minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                marginBottom: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {record.name}
            </div>
            <div
              style={{
                fontSize: 12,
                color: '#8c8c8c',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {record.remark || t('emptyNoDescription')}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t('fieldType'),
      key: 'category',
      width: 100,
      render: (_: unknown, record: ProductGroup) => {
        const { code, desc } = pickProductGroupCategoryFields(record);
        return <Tag color="blue">{getProductGroupCategoryLabel(code, desc)}</Tag>;
      },
    },
    {
      title: t('fieldStatus'),
      dataIndex: 'isShow',
      key: 'isShow',
      width: 100,
      render: (isShow: number) => (
        <Tag color={isShow === 1 ? 'green' : 'red'}>
          {isShow === 1 ? t('statusOnline') : t('statusOffline')}
        </Tag>
      ),
    },
    {
      title: t('relatedProductCount'),
      key: 'productCount',
      width: 120,
      render: (record: ProductGroup) => (
        <span>{record.productCount ?? 0} {t('unitCount')}</span>
      ),
    },
    {
      title: t('fieldCreateTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 180,
      render: (date: string) => formatDateTime(date),
    },
    {
      title: t('fieldActions'),
      key: 'action',
      width: 200,
      render: (record: ProductGroup) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record.id)}
          >
            {t('actionView')}
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record.id)}
          >
            {t('actionEdit')}
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            {t('actionDelete')}
          </Button>
        </Space>
      ),
    },
  ], [t]);

  const expandedProductColumns = useMemo(() => [
    {
      title: t('fieldProductCode'),
      dataIndex: 'id',
      key: 'id',
      width: 120,
      fixed: 'left' as const,
      render: (id: string) => (
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: '12px',
            color: '#666',
            backgroundColor: '#f5f5f5',
            padding: '2px 6px',
            borderRadius: '4px',
          }}
        >
          {id}
        </span>
      ),
    },
    {
      title: t('fieldLogo'),
      dataIndex: 'logoUrl',
      key: 'logoUrl',
      width: 80,
      render: (logoUrl: string, record: Product) =>
        logoUrl ? (
          <Image
            width={40}
            height={40}
            src={logoUrl}
            alt={record.name}
            style={{ borderRadius: 4, objectFit: 'cover' }}
          />
        ) : (
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 4,
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              fontSize: 10,
            }}
          >
            {t('emptyNone')}
          </div>
        ),
    },
    {
      title: t('fieldProductName'),
      dataIndex: 'name',
      key: 'name',
      width: 160,
      ellipsis: true,
    },
    {
      title: t('fieldProductType'),
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (_: string, record: Product) => (
        <Tag color="blue">{getCategoryLabel(record.category, record.categoryDesc)}</Tag>
      ),
    },
    {
      title: t('fieldChildCategory'),
      dataIndex: 'childCategory',
      key: 'childCategory',
      width: 100,
      render: (_: string, record: Product) => {
        const { code, desc } = pickChildCategoryFields(record);
        return <Tag color="green">{getChildCategoryLabel(code, desc)}</Tag>;
      },
    },
    {
      title: t('fieldOriginalPrice'),
      dataIndex: 'price',
      key: 'price',
      width: 90,
      render: (price: number) => `¥${price?.toFixed(2) || '0.00'}`,
    },
    {
      title: t('fieldDiscountPrice'),
      dataIndex: 'discountPrice',
      key: 'discountPrice',
      width: 90,
      render: (discountPrice: number) => `¥${discountPrice?.toFixed(2) || '0.00'}`,
    },
    {
      title: t('fieldStatus'),
      dataIndex: 'isShow',
      key: 'isShow',
      width: 90,
      render: (isShow: number) => (
        <Tag color={isShow === 1 ? 'green' : 'red'}>
          {isShow === 1 ? t('statusOnline') : t('statusOffline')}
        </Tag>
      ),
    },
    {
      title: t('fieldSort'),
      dataIndex: 'seq',
      key: 'seq',
      width: 70,
    },
    {
      title: t('fieldCreateTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 170,
      render: (date: string) => formatDateTime(date),
    },
    {
      title: t('fieldModifyTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      width: 170,
      render: (date: string) => formatDateTime(date),
    },
  ], [t]);

  const renderExpandedProducts = (record: ProductGroup) => {
    const products = expandedProducts[record.id];
    const loading = expandingGroupIds.has(record.id) && products === undefined;

    if (loading) {
      return (
        <div style={{ padding: '16px 48px', textAlign: 'center' }}>
          <Spin size="small" />
        </div>
      );
    }

    if (!products || products.length === 0) {
      return (
        <div style={{ padding: '8px 48px' }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('emptyNoRelatedProducts')} />
        </div>
      );
    }

    return (
      <Table
        columns={expandedProductColumns}
        dataSource={products}
        rowKey="id"
        pagination={false}
        size="small"
        scroll={{ x: 1300 }}
        style={{ margin: '0 48px 8px' }}
      />
    );
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('productGroupManagement')}</h1>
      </div>

      <Card className="content-card">
        <div className="page-actions">
          <Search
            placeholder={t('searchProductGroupName')}
            allowClear
            enterButton={<SearchOutlined />}
            size="middle"
            onSearch={handleSearch}
            style={{ width: 300 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            {t('addProductGroup')}
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={productGroups}
          loading={loading}
          rowKey="id"
          pagination={false}
          scroll={{ x: 800 }}
          expandable={{
            expandedRowRender: renderExpandedProducts,
            onExpand: (expanded, record) => {
              if (expanded) {
                loadGroupProducts(record.id);
              }
            },
          }}
        />

        <div className="pagination-wrapper">
          <Pagination
            current={current}
            total={total}
            pageSize={pageSize}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              t('paginationRange', { from: range[0], to: range[1], total })
            }
            onChange={(page, size) => {
              setCurrent(page);
              setPageSize(size || 20);
            }}
            onShowSizeChange={(current, size) => {
              setCurrent(1);
              setPageSize(size);
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default ProductGroupList;
